import {
  AfterContentInit,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { NgxSmartModalService } from "ngx-smart-modal";
import { AuthService } from "src/app/SayTS/auth.service";
import { SHeaderComponent } from "src/app/SayTS/comps/header.component";
import { HttpPlus } from "src/app/SayTS/http-plus";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { SharedService } from "src/app/_shared/shared.service";
import { Location } from "@angular/common";
import { filter, take, Subject, takeUntil } from "rxjs";
import { Fce } from "src/app/SayTS/fce";
import { Title } from "@angular/platform-browser";
import { DbConstants } from "src/app/_consts/db-constants";
import { environment } from "src/environments/environment";
import { TimerPlus } from "../SayTS/objects/TimerPlus";
import {
  IResponsibilityAssignment,
  ResponsibilityAssignment,
  RESPONSIBILITY_ROLE_TRANSLATE_PREFIX,
  RESPONSIBILITY_ROLES,
  RESPONSIBILITY_ROLES_RESTRICTED,
  ResponsibilityRole,
} from "../_models/responsibility-assignment";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";
import dateFormats from "../SayTS/const/date-formats";
import { MyDatePipe } from "../SayTS/directives/my-date";
import { TranslateService } from "@ngx-translate/core";
import { RouterLinkAccessDirective } from "../_components/directives/router-link-access.directive";
import { ScrollFadeDirective } from "../_components/directives/scroll-fade.directive";
import { LoggedUserService } from "../_services/logged-user.service";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { NgClass } from "@angular/common";
import {
  RoleServiceStatus,
  ResponsibilityStatusService,
} from "../_services/responsibility-status.service";
import { Role } from "../_models/Role";
import { ThemeService } from "../_services/theme.service";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    NgbModule,
    MyDatePipe,
    RouterLinkAccessDirective,
    ScrollFadeDirective,
    NgbTooltip,
    NgClass,
  ],
})
export class HeaderComponent
  extends SHeaderComponent
  implements AfterContentInit, OnDestroy
{
  locationName: string;
  currentResponsible: IResponsibilityAssignment[] = [];
  readonly rolePrefix = RESPONSIBILITY_ROLE_TRANSLATE_PREFIX;
  ticketAttentionCount = 0;
  recordsAttentionCount = 0;
  batteryErrorCount = 0;
  skidErrorCount = 0;
  fveErrorCount = 0;
  twinChargerErrorCount = 0;
  emsPortalErrorCount = 0;
  alertErrorCount = 0;
  private ticketTimer: any = null;
  private destroy$ = new Subject<void>();
  roleStatuses: RoleServiceStatus[] = [];

  private translate = inject(TranslateService);
  public theme = inject(ThemeService);

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    public shared: SharedService,
    protected http: HttpPlus,
    protected auth: AuthService,
    protected modal: NgxSmartModalService,
    protected location: Location,
    protected offcanvasService: NgbOffcanvas,
    private titleService: Title,
    public loggedUser: LoggedUserService,
    private statusService: ResponsibilityStatusService,
  ) {
    super(
      activatedRoute,
      router,
      shared,
      http,
      auth,
      modal,
      location,
      offcanvasService,
    );

    DbConstants.CONSTANTS_LOADED.pipe(
      filter((loaded) => loaded),
      take(1),
    ).subscribe(() => {
      this.locationName = DbConstants.LOCATION_NAME;
      if (!Fce.empty(this.locationName)) {
        this.titleService.setTitle(`${this.locationName}`);
      }
    });
  }

  get shownRolesStatuses(): ResponsibilityRole[] {
    if (this.loggedUser.hasModule("ViewRestricted"))
      return RESPONSIBILITY_ROLES_RESTRICTED;

    return RESPONSIBILITY_ROLES;
  }

  ngAfterContentInit(): void {
    this.shared.responsibilityChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadCurrentResponsible();
      });
    // načíst počet jakmile jsou k dispozici práva/login (řeší i první načtení bez navigace)
    this.shared.rightsAllWatch.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadCurrentResponsible();
      this.loadTicketAttention();
      this.loadRecordsAttention();
      this.loadErrorCounts();
    });
    // obnovit počet při každé navigaci + periodicky každou minutu
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loadTicketAttention();
        this.loadRecordsAttention();
        this.loadErrorCounts();
      });
    this.ticketTimer = setInterval(() => {
      this.loadTicketAttention();
      this.loadRecordsAttention();
      this.loadErrorCounts();
    }, 60000);
  }

  ngOnDestroy(): void {
    if (this.ticketTimer) clearInterval(this.ticketTimer);

    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Radek v menu je oriznuty na jmeno, takze role i cislo musi zustat v tooltipu. */
  respoTitle(current: IResponsibilityAssignment): string {
    const role = this.shared.t(this.rolePrefix + current.role);
    const phone = current.user?.phone ? ` · ${current.user.phone}` : "";
    return `${role}: ${current.user?.fullname}${phone}`;
  }

  /** Sbalena lista ma jen jednu ikonu - do tooltipu proto patri obe role naraz. */
  get currentResponsibleTitle(): string {
    return this.roleStatuses
      .filter((s) => s.current)
      .map((s) => this.respoTitle(s.current!))
      .join("\n");
  }

  get hasAnyEndingGap(): boolean {
    return this.roleStatuses.some((s) => !!s.endingGap);
  }

  /** Počet tiketů, kde jsem Asignee a chtějí pozornost (otevřené). */
  async loadTicketAttention(): Promise<void> {
    if (
      !this.shared.isLoggedIn() ||
      !(
        this.shared.canView("TICKETS") ||
        this.shared.isAdmin() ||
        this.loggedUser.hasModule("TICKETS")
      )
    ) {
      this.ticketAttentionCount = 0;
      return;
    }
    const r: any = await this.http.get("tickets/my_count", null, true);
    this.ticketAttentionCount = r?.count || 0;
  }

  /**
   * Červené odznaky chyb v menu: baterie v ERROR stavu (BMS/PCS alarm), skidy
   * s chybovou subbaterií či režimem ERROR, FVE/TwinChargery/EMS portály
   * v červeném stavu své přehledové stránky a lokace vyžadující pozornost ve
   * Správě upozornění. ViewRestricted chyby vidět nesmí — endpoint mu vrací
   * nuly a navíc se ani nevolá.
   */
  async loadErrorCounts(): Promise<void> {
    if (
      !this.shared.isLoggedIn() ||
      this.loggedUser.hasModule("ViewRestricted")
    ) {
      this.batteryErrorCount = 0;
      this.skidErrorCount = 0;
      this.fveErrorCount = 0;
      this.twinChargerErrorCount = 0;
      this.emsPortalErrorCount = 0;
      this.alertErrorCount = 0;
      return;
    }
    const r: any = await this.http.get("batteries/error_counts", null, true);
    this.batteryErrorCount = r?.batteries || 0;
    this.skidErrorCount = r?.skids || 0;
    this.fveErrorCount = r?.fves || 0;
    this.twinChargerErrorCount = r?.twin_chargers || 0;
    this.emsPortalErrorCount = r?.ems_portals || 0;
    this.alertErrorCount = r?.alerts || 0;
  }

  /** Počet servisních požadavků, kde jsem řešitel a jsou otevřené. */
  async loadRecordsAttention(): Promise<void> {
    if (!this.shared.isLoggedIn() || !this.loggedUser.hasModule("TICKETS")) {
      this.recordsAttentionCount = 0;
      return;
    }
    const r: any = await this.http.get(
      "batteries/records/my_count",
      null,
      true,
    );
    this.recordsAttentionCount = r?.count || 0;
  }

  navigateToShiftPlanner() {
    this.router.navigate(["/responsibility-planner"]);
  }

  async loadCurrentResponsible() {
    // Endpoint je za auth — nepřihlášeně (/login) vracel 401 a toast "Incorrect login details".
    if (!this.shared.isLoggedIn()) {
      this.roleStatuses = [];
      return;
    }
    this.roleStatuses = await this.statusService.load();
  }

  gapTooltip(status: RoleServiceStatus): string {
    if (!status.endingGap) return "";
    const gap = status.endingGap;

    if (gap.to && gap.from === gap.to) {
      return this.translate.instant("gap_notice_single_day_short", {
        from: gap.from,
      });
    } else if (gap.to) {
      return this.translate.instant("gap_notice_with_end_short", {
        from: gap.from,
        to: gap.to,
      });
    } else {
      return this.translate.instant("gap_notice_no_end_short", {
        from: gap.from,
      });
    }
  }
  roleColorStyle(role: ResponsibilityRole): string {
    return this.statusService.roleColorStyle(role);
  }
}
